// Training Page with 5 tabs: Library, Workshops, Participation, Training Requests, My Reflections
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { trainingApi, trainingMaterialsApi, trainingWorkshopsApi } from '../services/api';
import api from '../services/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  BookOpen, Search, Upload, Trash2, FileText, Plus, 
  Calendar, MapPin, Users, CheckCircle2, Star, GraduationCap,
  ClipboardCheck, MessageSquare, Lightbulb, ArrowUpRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrainingRequestsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const isSenior = user?.role === 'senior_facilitator';
  const [activeTab, setActiveTab] = useState('library');

  return (
    <Layout>
      <div className="space-y-6" data-testid="training-page">
        <div className="bg-gradient-to-r from-[#E6007E]/5 to-purple-50 rounded-2xl p-6 border border-pink-100">
          <h1 className="text-2xl font-bold text-[#2A1514]">
            {language === 'el' ? 'Εκπαίδευση' : 'Training'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'el' ? 'Βιβλιοθήκη, workshops, συμμετοχές & αναστοχασμοί' : 'Library, workshops, participation & reflections'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 h-10 bg-gray-100/80 rounded-xl p-1">
            <TabsTrigger value="library" className="text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-library">
              <BookOpen size={13} className="mr-1.5" />
              {language === 'el' ? 'Βιβλιοθήκη' : 'Library'}
            </TabsTrigger>
            <TabsTrigger value="workshops" className="text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-workshops">
              <GraduationCap size={13} className="mr-1.5" />
              Workshops
            </TabsTrigger>
            <TabsTrigger value="participation" className="text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-participation">
              <Users size={13} className="mr-1.5" />
              {language === 'el' ? 'Συμμετοχές' : 'Participation'}
            </TabsTrigger>
            <TabsTrigger value="requests" className="text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-requests">
              <ClipboardCheck size={13} className="mr-1.5" />
              {language === 'el' ? 'Αιτήματα' : 'Requests'}
            </TabsTrigger>
            <TabsTrigger value="reflections" className="text-xs rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-reflections">
              <Lightbulb size={13} className="mr-1.5" />
              {language === 'el' ? 'Σκέψεις μου' : 'My Reflections'}
            </TabsTrigger>
          </TabsList>

          <div className="mt-4">
            <TabsContent value="library"><LibraryTab isAdmin={isAdmin} language={language} /></TabsContent>
            <TabsContent value="workshops"><WorkshopsTab isAdmin={isAdmin} language={language} user={user} /></TabsContent>
            <TabsContent value="participation"><ParticipationTab isAdmin={isAdmin} language={language} user={user} /></TabsContent>
            <TabsContent value="requests"><RequestsTab isAdmin={isAdmin} isSenior={isSenior} language={language} user={user} /></TabsContent>
            <TabsContent value="reflections"><ReflectionsTab language={language} user={user} /></TabsContent>
          </div>
        </Tabs>
      </div>
    </Layout>
  );
}

// ==================== LIBRARY TAB ====================
function LibraryTab({ isAdmin, language }) {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', category: '', tags: '' });
  const [file, setFile] = useState(null);

  const loadMaterials = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCat) params.category = filterCat;
      const res = await trainingMaterialsApi.getAll(params);
      setMaterials(res.data.materials || []);
      setCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
  }, [search, filterCat]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const handleUpload = async () => {
    if (!file || !uploadForm.title || !uploadForm.category) {
      toast.error(language === 'el' ? 'Συμπλήρωσε τίτλο, κατηγορία και αρχείο' : 'Fill title, category and file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', uploadForm.title);
      fd.append('category', uploadForm.category);
      fd.append('tags', uploadForm.tags);
      fd.append('file', file);
      await trainingMaterialsApi.upload(fd);
      toast.success(language === 'el' ? 'Ανέβηκε!' : 'Uploaded!');
      setShowUpload(false);
      setUploadForm({ title: '', category: '', tags: '' });
      setFile(null);
      loadMaterials();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'el' ? 'Διαγραφή αρχείου;' : 'Delete file?')) return;
    try {
      await trainingMaterialsApi.delete(id);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      loadMaterials();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-4" data-testid="library-tab">
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder={language === 'el' ? 'Αναζήτηση υλικού...' : 'Search materials...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="library-search"
              />
            </div>
            <Select value={filterCat || 'all'} onValueChange={(v) => setFilterCat(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="library-category-filter">
                <SelectValue placeholder={language === 'el' ? 'Κατηγορία' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'el' ? 'Όλες' : 'All'}</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Dialog open={showUpload} onOpenChange={setShowUpload}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#E6007E] hover:bg-[#C4006A] h-9 text-xs" data-testid="upload-material-btn">
                    <Upload size={14} className="mr-1.5" /> {language === 'el' ? 'Ανέβασμα' : 'Upload'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{language === 'el' ? 'Ανέβασμα Υλικού' : 'Upload Material'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{language === 'el' ? 'Τίτλος' : 'Title'}</Label>
                      <Input value={uploadForm.title} onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})} className="h-9 text-sm" data-testid="material-title" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{language === 'el' ? 'Κατηγορία' : 'Category'}</Label>
                      <Input value={uploadForm.category} onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})} placeholder={language === 'el' ? 'π.χ. Ηγεσία, Επικοινωνία' : 'e.g. Leadership, Communication'} className="h-9 text-sm" data-testid="material-category" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Tags ({language === 'el' ? 'χωρισμένα με κόμμα' : 'comma separated'})</Label>
                      <Input value={uploadForm.tags} onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})} placeholder="team, leadership, skills" className="h-9 text-sm" data-testid="material-tags" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">PDF</Label>
                      <Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} className="h-9 text-sm" data-testid="material-file" />
                    </div>
                    <Button onClick={handleUpload} disabled={uploading} className="w-full bg-[#E6007E] hover:bg-[#C4006A]" data-testid="submit-upload-btn">
                      {uploading ? '...' : (language === 'el' ? 'Ανέβασμα' : 'Upload')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardContent>
      </Card>

      {materials.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center">
          <BookOpen size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">{language === 'el' ? 'Δεν βρέθηκε υλικό' : 'No materials found'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {materials.map(m => (
            <Card key={m.material_id} className="border-0 shadow-sm hover:shadow-md transition-all group">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-red-100 transition-colors">
                    <FileText className="text-red-500" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await trainingMaterialsApi.download(m.material_id);
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const w = window.open(url, '_blank');
                          if (!w) {
                            // popup blocked → trigger download instead
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = m.filename || `${m.title}.pdf`;
                            link.click();
                          }
                          setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                        } catch (e) {
                          toast.error(
                            e.response?.status === 404
                              ? (language === 'el' ? 'Το αρχείο δεν βρέθηκε. Πιθανόν χρειάζεται επανα-ανέβασμα.' : 'File not found. It may need to be re-uploaded.')
                              : (language === 'el' ? 'Σφάλμα λήψης αρχείου' : 'Failed to open file')
                          );
                        }
                      }}
                      className="text-sm font-medium text-[#2A1514] hover:text-[#E6007E] transition-colors line-clamp-1 flex items-center gap-1 text-left"
                      data-testid={`material-link-${m.material_id}`}
                    >
                      {m.title} <ArrowUpRight size={12} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                      <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">{m.category}</Badge>
                      {m.tags?.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{(m.file_size / 1024).toFixed(0)} KB</p>
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDelete(m.material_id)}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== WORKSHOPS TAB ====================
function WorkshopsTab({ isAdmin, language }) {
  const [workshops, setWorkshops] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showAttendance, setShowAttendance] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [form, setForm] = useState({ name: '', date: '', end_date: '', location: '', description: '', trainer: '', target_criteria: [] });
  const [criteria, setCriteria] = useState({});

  const loadWorkshops = useCallback(async () => {
    try {
      const res = await trainingWorkshopsApi.getAll();
      setWorkshops(res.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadWorkshops();
    api.get('/evaluations/criteria').then(r => setCriteria(r.data || {})).catch(() => {});
  }, [loadWorkshops]);

  const handleCreate = async () => {
    if (!form.name || !form.date) {
      toast.error(language === 'el' ? 'Συμπλήρωσε όνομα και ημερομηνία' : 'Fill name and date');
      return;
    }
    try {
      await trainingWorkshopsApi.create({
        name: form.name,
        date: new Date(form.date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        location: form.location || null,
        description: form.description || null,
        trainer: form.trainer || null,
        target_criteria: form.target_criteria || []
      });
      toast.success(language === 'el' ? 'Workshop δημιουργήθηκε!' : 'Workshop created!');
      setShowCreate(false);
      setForm({ name: '', date: '', end_date: '', location: '', description: '', trainer: '', target_criteria: [] });
      loadWorkshops();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleCriterion = (key) => {
    setForm(f => ({
      ...f,
      target_criteria: f.target_criteria.includes(key)
        ? f.target_criteria.filter(k => k !== key)
        : [...f.target_criteria, key]
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'el' ? 'Διαγραφή workshop;' : 'Delete workshop?')) return;
    try {
      await trainingWorkshopsApi.delete(id);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      loadWorkshops();
    } catch (e) { toast.error('Error'); }
  };

  const openAttendance = async (ws) => {
    try {
      const res = await trainingWorkshopsApi.getAttendance(ws.workshop_id);
      setAttendanceData(res.data || []);
      setShowAttendance(ws);
    } catch (e) { toast.error('Error loading attendance'); }
  };

  const saveAttendance = async () => {
    try {
      await trainingWorkshopsApi.updateAttendance(showAttendance.workshop_id, attendanceData);
      toast.success(language === 'el' ? 'Αποθηκεύτηκε!' : 'Saved!');
      setShowAttendance(null);
      loadWorkshops();
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="space-y-4" data-testid="workshops-tab">
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#E6007E] hover:bg-[#C4006A] h-9 text-xs" data-testid="create-workshop-btn">
                <Plus size={14} className="mr-1.5" /> {language === 'el' ? 'Νέο Workshop' : 'New Workshop'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{language === 'el' ? 'Νέο Training Workshop' : 'New Training Workshop'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{language === 'el' ? 'Τίτλος' : 'Title'}</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-9 text-sm" data-testid="workshop-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{language === 'el' ? 'Έναρξη' : 'Start'}</Label>
                    <Input type="datetime-local" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="h-9 text-sm" data-testid="workshop-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{language === 'el' ? 'Λήξη' : 'End'}</Label>
                    <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="h-9 text-sm" data-testid="workshop-end-date" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{language === 'el' ? 'Τοποθεσία' : 'Location'}</Label>
                    <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="h-9 text-sm" data-testid="workshop-location" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{language === 'el' ? 'Εκπαιδευτής' : 'Trainer'}</Label>
                    <Input value={form.trainer} onChange={(e) => setForm({...form, trainer: e.target.value})} className="h-9 text-sm" data-testid="workshop-trainer" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">{language === 'el' ? 'Περιγραφή' : 'Description'}</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="text-sm" data-testid="workshop-description" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium">
                      {language === 'el' ? 'Στοχεύει στα κριτήρια' : 'Targets criteria'}
                    </Label>
                    <span className="text-[10px] text-gray-400">
                      {form.target_criteria.length} {language === 'el' ? 'επιλεγμένα' : 'selected'}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400 -mt-1">
                    {language === 'el'
                      ? 'Επίλεξε ποια κριτήρια αξιολόγησης ενισχύει αυτό το workshop. Χρησιμοποιείται για να συστηθεί αυτόματα στο Προφίλ Ανάπτυξης κάθε facilitator.'
                      : 'Tag which evaluation criteria this workshop strengthens. Used to auto-recommend it on each facilitator\'s Development Profile.'}
                  </p>
                  <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 p-2 space-y-2">
                    {Object.entries(criteria).map(([catKey, cat]) => (
                      <div key={catKey}>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                          {language === 'el' ? cat.el : cat.en}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(cat.criteria || []).map(c => {
                            const active = form.target_criteria.includes(c.key);
                            return (
                              <button
                                key={c.key}
                                type="button"
                                onClick={() => toggleCriterion(c.key)}
                                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                                  active
                                    ? 'bg-[#E6007E] text-white border-[#E6007E]'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#E6007E]/40'
                                }`}
                                data-testid={`criterion-toggle-${c.key}`}
                              >
                                {language === 'el' ? c.el : c.en}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#E6007E] hover:bg-[#C4006A]" data-testid="submit-workshop-btn">
                  {language === 'el' ? 'Δημιουργία' : 'Create'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {workshops.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center">
          <GraduationCap size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">{language === 'el' ? 'Δεν υπάρχουν workshops' : 'No workshops yet'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {workshops.map(ws => (
            <Card key={ws.workshop_id} className="border-0 shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-sm text-[#2A1514]">{ws.name}</h3>
                      {ws.calendar_event_id && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-600 border-green-200">Calendar</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                      <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(ws.date).toLocaleDateString('el-GR')}</span>
                      {ws.location && <span className="flex items-center gap-1"><MapPin size={11} /> {ws.location}</span>}
                      {ws.trainer && <span className="flex items-center gap-1"><GraduationCap size={11} /> {ws.trainer}</span>}
                      <span className="flex items-center gap-1"><Users size={11} /> <strong>{ws.attendee_count || 0}</strong> {language === 'el' ? 'συμμετοχές' : 'attended'}</span>
                    </div>
                    {ws.target_criteria && ws.target_criteria.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {ws.target_criteria.slice(0, 6).map(k => {
                          const cat = Object.values(criteria).find(c => (c.criteria || []).some(x => x.key === k));
                          const c = cat?.criteria.find(x => x.key === k);
                          if (!c) return null;
                          return (
                            <Badge key={k} variant="outline" className="text-[9px] py-0 px-1.5 border-purple-200 text-purple-700 bg-purple-50">
                              {language === 'el' ? c.el : c.en}
                            </Badge>
                          );
                        })}
                        {ws.target_criteria.length > 6 && (
                          <span className="text-[9px] text-gray-400">+{ws.target_criteria.length - 6}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="h-8 text-xs border-[#E6007E]/30 text-[#E6007E] hover:bg-[#E6007E]/5" onClick={() => openAttendance(ws)} data-testid={`attendance-btn-${ws.workshop_id}`}>
                        <CheckCircle2 size={13} className="mr-1" />
                        {language === 'el' ? 'Παρουσίες' : 'Attendance'}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={() => handleDelete(ws.workshop_id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Attendance Dialog */}
      <Dialog open={!!showAttendance} onOpenChange={(open) => !open && setShowAttendance(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {language === 'el' ? 'Παρουσίες & Αξιολόγηση' : 'Attendance & Evaluation'} — {showAttendance?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Facilitator</TableHead>
                  <TableHead className="text-xs w-[70px]">{language === 'el' ? 'Παρών' : 'Present'}</TableHead>
                  <TableHead className="text-xs w-[90px]">{language === 'el' ? 'Βαθμός' : 'Rating'}</TableHead>
                  <TableHead className="text-xs">{language === 'el' ? 'Σχόλιο' : 'Comment'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendanceData.map((a, idx) => (
                  <TableRow key={a.user_id}>
                    <TableCell className="text-xs font-medium">{a.name}</TableCell>
                    <TableCell>
                      <input type="checkbox" checked={a.attended} onChange={(e) => {
                        const u = [...attendanceData]; u[idx] = {...u[idx], attended: e.target.checked}; setAttendanceData(u);
                      }} className="h-4 w-4 rounded border-gray-300 accent-[#E6007E]" />
                    </TableCell>
                    <TableCell>
                      <Select value={String(a.rating || '')} onValueChange={(v) => {
                        const u = [...attendanceData]; u[idx] = {...u[idx], rating: v ? parseInt(v) : null}; setAttendanceData(u);
                      }}>
                        <SelectTrigger className="h-7 w-16 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input value={a.comment || ''} onChange={(e) => {
                        const u = [...attendanceData]; u[idx] = {...u[idx], comment: e.target.value}; setAttendanceData(u);
                      }} className="h-7 text-xs" placeholder={language === 'el' ? 'Σχόλιο...' : 'Comment...'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button onClick={saveAttendance} className="w-full mt-4 bg-[#E6007E] hover:bg-[#C4006A]" data-testid="save-attendance-btn">
              {language === 'el' ? 'Αποθήκευση' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== REQUESTS TAB ====================
function RequestsTab({ isAdmin, isSenior, language }) {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [reqRes, usersRes] = await Promise.all([
          trainingApi.getRequests(),
          api.get('/users').catch(() => ({ data: [] }))
        ]);
        setRequests(reqRes.data || []);
        const map = {};
        (usersRes.data || []).forEach(u => { map[u.user_id] = u; });
        setUsers(map);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">{language === 'el' ? 'Φόρτωση...' : 'Loading...'}</div>;

  return (
    <div className="space-y-3" data-testid="requests-tab">
      {requests.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center">
          <ClipboardCheck size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">{language === 'el' ? 'Δεν υπάρχουν αιτήματα εκπαίδευσης ακόμα' : 'No training requests yet'}</p>
          <p className="text-[11px] text-gray-400 mt-1">
            {language === 'el'
              ? 'Τα αιτήματα συγκεντρώνονται από τις αξιολογήσεις (πεδίο "Micro-trainings που χρειάζομαι")'
              : 'Requests are aggregated from evaluations (field "Micro-trainings I need")'}
          </p>
        </CardContent></Card>
      ) : (
        requests.map((r, i) => (
          <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-all" data-testid={`request-row-${i}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2A1514]">{r.training_name}</p>
                  {(isAdmin || isSenior) && r.requesters && r.requesters.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {r.requesters.map(uid => (
                        <Badge key={uid} variant="outline" className="text-[10px] py-0 px-1.5">
                          {users[uid]?.name || uid}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Badge className="bg-[#E6007E]/10 text-[#E6007E] border-0 text-xs shrink-0">
                  {r.count}× {language === 'el' ? 'αιτήματα' : 'requests'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// ==================== PARTICIPATION TAB ====================
function ParticipationTab({ isAdmin, language }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [myAssignments, setMyAssignments] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const res = await trainingWorkshopsApi.getParticipation(year);
      setData(res.data);
    } catch (e) { console.error(e); }
  }, [year]);

  useEffect(() => {
    loadData();
    if (!isAdmin) {
      trainingWorkshopsApi.getMyAssignments().then(r => setMyAssignments(r.data || [])).catch(() => {});
    }
  }, [loadData, isAdmin]);

  if (!data) return <div className="text-center py-12 text-gray-400 text-sm">{language === 'el' ? 'Φόρτωση...' : 'Loading...'}</div>;

  return (
    <div className="space-y-4" data-testid="participation-tab">
      {!isAdmin && myAssignments.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#E6007E] bg-gradient-to-r from-[#E6007E]/5 to-transparent" data-testid="my-recommended-workshops">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb size={14} className="text-[#E6007E]" />
              <h4 className="text-sm font-semibold text-[#2A1514]">
                {language === 'el' ? 'Προτεινόμενα για σένα' : 'Recommended for you'}
              </h4>
              <Badge variant="outline" className="text-[10px] border-[#E6007E]/30 text-[#E6007E]">
                {myAssignments.length}
              </Badge>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              {language === 'el'
                ? 'Workshops που σου πρότεινε ο διαχειριστής με βάση το προφίλ ανάπτυξής σου.'
                : 'Workshops your admin picked based on your development profile.'}
            </p>
            <div className="space-y-2">
              {myAssignments.map(a => (
                <div key={a.workshop_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2A1514] truncate">{a.name}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                      {a.date && <span>{new Date(a.date).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      {a.trainer && <span>· {a.trainer}</span>}
                    </div>
                    {a.criterion_labels && a.criterion_labels.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {a.criterion_labels.map((c, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] py-0 px-1.5 border-amber-200 text-amber-700 bg-amber-50">
                            {language === 'el' ? c.el : c.en}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {language === 'el' ? `Σύνολο workshops ${year}:` : `Total workshops ${year}:`}{' '}
            <span className="font-bold text-[#2A1514] text-lg">{data.total_workshops}</span>
          </p>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {data.users?.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center">
          <Users size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">{language === 'el' ? 'Δεν βρέθηκαν δεδομένα' : 'No data found'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.users.map(u => (
            <Card key={u.user_id} className="border-0 shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#2A1514]">{u.name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {u.role === 'senior_facilitator' ? 'Senior' : 'Facilitator'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1.5 text-gray-500">
                      <GraduationCap size={13} />
                      <span className="font-bold text-[#2A1514]">{u.trainings_attended}</span>
                      <span className="text-gray-400">/ {u.total_trainings}</span>
                    </span>
                    {u.average_rating > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 rounded-full">
                        <Star size={12} className="text-amber-500" />
                        <span className="font-bold text-amber-700">{u.average_rating}</span>
                      </span>
                    )}
                  </div>
                </div>
                {u.workshops?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {u.workshops.map((w, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px] px-3 py-1.5 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">{w.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">{new Date(w.date).toLocaleDateString('el-GR')}</span>
                          {w.rating && <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" variant="outline">{w.rating}/5</Badge>}
                          {w.comment && <span className="text-gray-500 max-w-[200px] truncate">{w.comment}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== MY REFLECTIONS TAB ====================
function ReflectionsTab({ language, user }) {
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/my-reflections');
        setReflections(res.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">{language === 'el' ? 'Φόρτωση...' : 'Loading...'}</div>;

  return (
    <div className="space-y-3" data-testid="reflections-tab">
      {reflections.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center">
          <Lightbulb size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">{language === 'el' ? 'Δεν υπάρχουν αναστοχασμοί ακόμα' : 'No reflections yet'}</p>
          <p className="text-[11px] text-gray-400 mt-1">{language === 'el' ? 'Θα εμφανιστούν μετά τις αξιολογήσεις σας' : 'They will appear after your evaluations'}</p>
        </CardContent></Card>
      ) : (
        reflections.map((r, i) => (
          <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">{r.event_name}</Badge>
                <span className="text-[10px] text-gray-400">{r.date ? new Date(r.date).toLocaleDateString('el-GR') : ''}</span>
              </div>
              {r.what_went_well && (
                <div className="mb-2 p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-[10px] font-semibold text-green-700 mb-1 flex items-center gap-1">
                    <CheckCircle2 size={11} />
                    {language === 'el' ? 'Τι πήγε καλά σήμερα;' : 'What went well today?'}
                  </p>
                  <p className="text-xs text-green-800">{r.what_went_well}</p>
                </div>
              )}
              {r.what_to_improve && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-[10px] font-semibold text-amber-700 mb-1 flex items-center gap-1">
                    <ArrowUpRight size={11} />
                    {language === 'el' ? 'Τι θα βελτιώσω στο επόμενο event/workshop;' : 'What will I improve next?'}
                  </p>
                  <p className="text-xs text-amber-800">{r.what_to_improve}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
